"use client";

import { useCallback, useEffect, useState } from "react";
import { usePathname } from "next/navigation";

/* How far a floating, bottom-anchored piece of chrome (the chat launcher) must
   lift to clear whatever is currently pinned to the bottom of the viewport —
   any element marked [data-bottom-bar]: today the cookie consent card and the
   services page's floating section dock.

   ⚠ MEASURED, NOT HARDCODED. The banner's height depends on where its copy
   wraps and on whether it is in "customise" mode, and it collapses into the
   corner button on scroll. A constant would either overlap it or leave the
   launcher floating too high everywhere else. We read the bar's own rect
   whenever it can change, which also naturally yields 0 once it has gone.

   ⚠ THE DEEPEST INTRUSION, NOT THE SUM, if more bars are ever marked: bars
   can overlap each other, so adding them would throw the chrome up the page.
   Mark any new fixed bottom bar with data-bottom-bar and it is picked up.

   ⚠ ONLY BARS THAT REACH THE RIGHT-HAND CORNER COUNT. Both bars are centred:
   on a phone they span the width and sit under the launcher, on a desktop
   they end well short of it, and lifting there would leave the launcher
   floating mid-air for nothing. CORNER is the widest the chat stack gets
   while closed (the greeting bubble) plus its inset.

   @param giveBack Pixels to sink back into the bar once lifted clear. Never
     express this as a PROPORTION of the bar's height: the consent copy wraps
     to several lines on a phone.
*/
const CORNER = 300;

export function useBottomBarLift(giveBack = 0) {
  const [lift, setLift] = useState(0);
  const pathname = usePathname();

  const measureLift = useCallback(() => {
    const bars = document.querySelectorAll<HTMLElement>("[data-bottom-bar]");
    let deepest = 0;
    bars.forEach((bar) => {
      const rect = bar.getBoundingClientRect();
      if (rect.right < window.innerWidth - CORNER) return;
      /* How much of the bar actually intrudes above the viewport bottom. A bar
         translated fully off-screen yields <= 0, i.e. no lift. */
      const intrusion = window.innerHeight - rect.top;
      if (intrusion > deepest) deepest = intrusion;
    });
    setLift(deepest > 0 ? Math.max(0, Math.round(deepest) - giveBack) : 0);
  }, [giveBack]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    let frame = 0;
    /* rAF-coalesced: the banner animates in and out, and we would otherwise
       measure mid-transition every frame. */
    const measure = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(measureLift);
    };
    measure();
    window.addEventListener("scroll", measure, { passive: true });
    window.addEventListener("resize", measure, { passive: true });

    /* The consent bar mounts after hydration (it waits on a localStorage read),
       changes height in customise mode, and unmounts on a choice — none of
       which fires scroll or resize. A ResizeObserver on the
       body catches all three, since each one reflows the document. */
    const ro = new ResizeObserver(measure);
    ro.observe(document.body);
    /* ...and the bar's own arrival/removal does not necessarily resize the
       body, because it is position:fixed. Watch for it directly. */
    const mo = new MutationObserver(measure);
    mo.observe(document.body, { childList: true, subtree: true });

    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener("scroll", measure);
      window.removeEventListener("resize", measure);
      ro.disconnect();
      mo.disconnect();
    };
  }, [pathname, measureLift]);

  return lift;
}
